import React, { useEffect, useState, useCallback } from 'react';
import { Avatar, Button, Card, Form, Input, Popconfirm, Space, Typography, Upload, message, Row, Col } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../config/api';

const { Title } = Typography;

const UserProfile = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [avatarUrl, setAvatarUrl] = useState('');

  const validatePassword = (_, value) => {
    if (!value) return Promise.reject(new Error('Password is required'));
    const minLength = value.length >= 8;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSymbol = /[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]/.test(value);
    
    if (!minLength) return Promise.reject(new Error('Password must be at least 8 characters'));
    if (!hasUppercase) return Promise.reject(new Error('Password must include at least one uppercase letter'));
    if (!hasLowercase) return Promise.reject(new Error('Password must include at least one lowercase letter'));
    if (!hasNumber) return Promise.reject(new Error('Password must include at least one number'));
    if (!hasSymbol) return Promise.reject(new Error('Password must include at least one symbol'));
    
    return Promise.resolve();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/me`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load profile');
      const data = await res.json();
      form.setFieldsValue({ name: data.name || '', email: data.email || '' });
      setAvatarUrl(data.avatar_url || '');
    } catch (e) {
      message.error(e.message || 'Error loading profile');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await fetch(`${apiBase}/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(values),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Update failed');
      message.success('Profile updated');
      // keep localStorage user in sync if present
      try {
        const userRaw = localStorage.getItem('user');
        if (userRaw) {
          const u = JSON.parse(userRaw);
          localStorage.setItem('user', JSON.stringify({ ...u, name: body.name, email: body.email, avatar_url: body.avatar_url }));
          window.dispatchEvent(new Event('userUpdated'));
        }
      } catch {}
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const removeAvatar = async () => {
    try {
      const res = await fetch(`${apiBase}/me/avatar`, { method: 'DELETE', headers: { ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to remove avatar');
      setAvatarUrl('');
      message.success('Avatar removed');
    } catch (e) {
      message.error(e.message || 'Error removing avatar');
    }
  };

  const uploadProps = {
    name: 'avatar',
    showUploadList: false,
    accept: 'image/png,image/jpeg,image/jpg,image/gif,image/webp',
    beforeUpload: (file) => {
      const isImage = /image\/(png|jpeg|jpg|gif|webp)/i.test(file.type);
      if (!isImage) {
        message.error('Only image files are allowed');
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const fd = new FormData();
        fd.append('avatar', file);
        const res = await fetch(`${apiBase}/me/avatar`, {
          method: 'POST',
          headers: { ...authHeaders() },
          body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Upload failed');
        setAvatarUrl(body.avatar_url || '');
        // sync localStorage user
        try {
          const userRaw = localStorage.getItem('user');
          if (userRaw) {
            const u = JSON.parse(userRaw);
            localStorage.setItem('user', JSON.stringify({ ...u, avatar_url: body.avatar_url }));
            window.dispatchEvent(new Event('userUpdated'));
          }
        } catch {}
        message.success('Avatar updated');
        onSuccess && onSuccess(body, file);
      } catch (err) {
        message.error(err.message || 'Upload error');
        onError && onError(err);
      }
    },
  };

  const changePassword = async () => {
    try {
      const { oldPassword, newPassword } = await pwdForm.validateFields();
      setPwdSaving(true);
      const res = await fetch(`${apiBase}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Password change failed');
      message.success('Password updated');
      pwdForm.resetFields();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Error changing password');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>My Profile</Title>

      <Row gutter={[16, 16]}>
        {/* Profile Information Card */}
        <Col xs={24} lg={12}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
            loading={loading}
          >
            {/* Header with user icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#3730a322', 
                  color: '#3730a3', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <UserOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Profile Information</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Update your personal details</div>
                </div>
              </div>
            </div>

            {/* Avatar Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 16, 
              padding: '16px', 
              background: '#f8fafc', 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              <Avatar size={64} src={avatarUrl ? `${apiBase}${avatarUrl}` : undefined}>
                {!avatarUrl && (form.getFieldValue('name') || '?').charAt(0).toUpperCase()}
              </Avatar>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                  Profile Picture
                </div>
                <Space>
                  <Upload {...uploadProps}>
                    <Button size="small">Change Avatar</Button>
                  </Upload>
                  {avatarUrl ? (
                    <Popconfirm title="Remove avatar?" onConfirm={removeAvatar} okText="Yes" cancelText="No">
                      <Button danger size="small">Remove</Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              </div>
            </div>

            {/* Profile Form */}
            <Form form={form} layout="vertical">
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter your name' }]}> 
                <Input placeholder="Your name" />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                <Input placeholder="your@email.com" />
              </Form.Item>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button type="primary" onClick={save} loading={saving} style={{ flex: 1, minWidth: 'auto' }}>
                  Save Changes
                </Button>
                <Button onClick={load} disabled={loading}>
                  Reload
                </Button>
              </div>
            </Form>
          </Card>
        </Col>

        {/* Password Change Card */}
        <Col xs={24} lg={12}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
          >
            {/* Header with lock icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#dc262622', 
                  color: '#dc2626', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <LockOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Security</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Change your password</div>
                </div>
              </div>
            </div>

            {/* Security Info */}
            <div style={{ 
              padding: '12px 16px', 
              background: '#fef2f2', 
              borderRadius: 8, 
              marginBottom: 16,
              border: '1px solid #fecaca'
            }}>
              <div style={{ fontSize: 13, color: '#991b1b', marginBottom: 4 }}>
                <strong>Security Notice</strong>
              </div>
              <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4 }}>
                Use a strong password with at least 8 characters including letters, numbers, and symbols.
              </div>
            </div>

            {/* Password Form */}
            <Form form={pwdForm} layout="vertical">
              <Form.Item name="oldPassword" label="Current Password" rules={[{ required: true, message: 'Enter current password' }]}>
                <Input.Password placeholder="Current password" />
              </Form.Item>
              <Form.Item name="newPassword" label="New Password" rules={[{ required: true, message: 'Enter new password' }, { validator: validatePassword }]}>
                <Input.Password placeholder="New password" />
              </Form.Item>
              <Button type="primary" onClick={changePassword} loading={pwdSaving} style={{ width: '100%' }}>
                Update Password
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfile;
